import { Injectable, computed, signal } from '@angular/core';

import { environment } from 'src/environments/environment';

/** What `assets/version.json` carries. Everything but the build time is best effort. */
export interface AppBuildInfo {
  buildTime: string | null;
  commit: string | null;
  branch: string | null;
  environment: string | null;
}

/** What a check against the deployed version file concluded. */
export type AppBuildCheck = 'current' | 'stale' | 'error';

/** Largest unit first: the first one the age clears is the one that gets used. */
const AGE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly buildInfoSignal = signal<AppBuildInfo | null>(null);
  private readonly relativeTimeFormat = new Intl.RelativeTimeFormat('en-AU', { numeric: 'auto' });

  /**
   * The build this tab is running, latched the first time it is read. Later
   * deploys change the file on the server but not what the admin is looking at,
   * so this deliberately does not follow it — AppUpdateService is what notices
   * the difference and offers the reload.
   */
  readonly buildInfo = this.buildInfoSignal.asReadonly();

  /** One line an admin can read back to us: "20 Aug 2026, 8:24 am · a1b2c3d". */
  readonly versionLabel = computed(() => {
    const info = this.buildInfoSignal();

    if (!info) {
      return null;
    }

    const parts = [this.formatBuildTime(info.buildTime), info.commit].filter((part): part is string => !!part);

    return parts.length ? parts.join(' · ') : null;
  });

  /** "20 Aug 2026, 12:24 pm" — when this build was stamped, or null if unknown. */
  readonly buildTimeLabel = computed(() => this.formatBuildTime(this.buildInfoSignal()?.buildTime ?? null));

  /** The same details spelled out, for pasting into a bug report. */
  readonly versionDetails = computed(() => {
    const info = this.buildInfoSignal();
    const lines = [`${environment.appName} frontend`, `Environment: ${info?.environment ?? environment.name}`];

    const buildTime = this.formatBuildTime(info?.buildTime ?? null);
    if (buildTime) {
      lines.push(`Build: ${buildTime}`);
    }

    if (info?.commit) {
      lines.push(`Commit: ${info.commit}${info.branch ? ` (${info.branch})` : ''}`);
    }

    return lines.join('\n');
  });

  /** Reads the deployed version file once and keeps that first answer. */
  async load(): Promise<AppBuildInfo | null> {
    const loaded = this.buildInfoSignal();
    if (loaded) {
      return loaded;
    }

    const info = await this.fetchBuildInfo();
    if (info) {
      this.buildInfoSignal.set(info);
    }

    return info;
  }

  /**
   * How old the running build is — "2 hours ago" — so a tab left open for a
   * week is obvious at a glance. Null when there is no build time to age.
   */
  buildAge(now = Date.now()): string | null {
    const date = this.toDate(this.buildInfoSignal()?.buildTime ?? null);

    if (!date) {
      return null;
    }

    const elapsedMs = now - date.getTime();

    for (const [unit, unitMs] of AGE_UNITS) {
      if (elapsedMs >= unitMs) {
        return this.relativeTimeFormat.format(-Math.floor(elapsedMs / unitMs), unit);
      }
    }

    // Under a minute old, or stamped in the future by a skewed clock.
    return 'just now';
  }

  /**
   * Compares what is deployed right now with what this tab booted with, so an
   * admin can tell a stale tab from a bug without waiting for the update poll.
   */
  async checkForNewerBuild(): Promise<AppBuildCheck> {
    const running = this.buildInfoSignal() ?? (await this.load());
    const deployed = await this.fetchBuildInfo();

    if (!running?.buildTime || !deployed?.buildTime) {
      return 'error';
    }

    return deployed.buildTime === running.buildTime ? 'current' : 'stale';
  }

  /**
   * Always goes to the network: update polling needs what is deployed right
   * now, not what this tab booted with.
   */
  async fetchBuildInfo(): Promise<AppBuildInfo | null> {
    try {
      const response = await fetch(`/assets/version.json?t=${Date.now()}`);
      if (!response.ok) return null;

      const data = await response.json();

      return {
        buildTime: this.readString(data?.buildTime),
        commit: this.readString(data?.commit),
        branch: this.readString(data?.branch),
        environment: this.readString(data?.environment) ?? environment.name,
      };
    } catch {
      return null;
    }
  }

  private formatBuildTime(buildTime: string | null): string | null {
    const date = this.toDate(buildTime);

    if (!date) {
      return null;
    }

    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }

  /** Build times are stamped as epoch milliseconds; an ISO string is accepted too. */
  private toDate(buildTime: string | null): Date | null {
    if (!buildTime) {
      return null;
    }

    const epochMs = Number(buildTime);
    const date = Number.isFinite(epochMs) && epochMs > 0 ? new Date(epochMs) : new Date(buildTime);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private readString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }
}
